/**
 * 数字格式化工具函数
 * 将大数字格式化为 K/M 单位显示
 */

interface FormatNumberOptions {
	/**
	 * 是否使用紧凑格式（K/M）
	 * @default true
	 */
	compact?: boolean;
	/**
	 * 小数点位数
	 * @default 1
	 */
	decimals?: number;
	/**
	 * 语言
	 * @default 'zh'
	 */
	language?: "zh" | "en";
}

/**
 * 格式化数字为紧凑格式（K/M）
 *
 * @param value - 要格式化的数字
 * @param options - 格式化选项
 * @returns 格式化后的字符串
 *
 * @example
 * formatNumber(1000) // "1K"
 * formatNumber(1500) // "1.5K"
 * formatNumber(30000) // "30K"
 * formatNumber(1000000) // "1M"
 * formatNumber(1500000) // "1.5M"
 * formatNumber(500) // "500"
 */
export function formatNumber(
	value: number | bigint,
	options: FormatNumberOptions = {},
): string {
	const { compact = true, decimals = 1, language = "zh" } = options;

	const numValue = typeof value === "bigint" ? Number(value) : value;

	// 如果不使用紧凑格式，直接返回千分位格式
	if (!compact) {
		return numValue.toLocaleString();
	}

	// K (千) - 1,000+
	if (numValue >= 1000 && numValue < 1000000) {
		const kValue = numValue / 1000;
		// 如果是整数，不显示小数点
		if (kValue % 1 === 0) {
			return `${kValue}K`;
		}
		return `${kValue.toFixed(decimals)}K`;
	}

	// M (百万) - 1,000,000+
	if (numValue >= 1000000 && numValue < 1000000000) {
		const mValue = numValue / 1000000;
		if (mValue % 1 === 0) {
			return `${mValue}M`;
		}
		return `${mValue.toFixed(decimals)}M`;
	}

	// B (十亿) - 1,000,000,000+
	if (numValue >= 1000000000) {
		const bValue = numValue / 1000000000;
		if (bValue % 1 === 0) {
			return `${bValue}B`;
		}
		return `${bValue.toFixed(decimals)}B`;
	}

	// 小于 1000，直接显示
	return numValue.toLocaleString();
}

/**
 * 格式化金额（带单位）
 *
 * @param value - 金额数值
 * @param unit - 单位（如 "DD", "SATS"）
 * @param options - 格式化选项
 * @returns 格式化后的字符串
 *
 * @example
 * formatAmount(30000, "DD") // "30K DD"
 * formatAmount(1500000, "SATS") // "1.5M SATS"
 */
export function formatAmount(
	value: number | bigint,
	unit: string,
	options: FormatNumberOptions = {},
): string {
	return `${formatNumber(value, options)} ${unit}`;
}

/**
 * 格式化范围（带单位）
 *
 * @param min - 最小值
 * @param max - 最大值
 * @param unit - 单位
 * @param options - 格式化选项
 * @returns 格式化后的字符串
 *
 * @example
 * formatRange(1000, 5000, "DD") // "1K - 5K DD"
 */
export function formatRange(
	min: number | bigint,
	max: number | bigint,
	unit: string,
	options: FormatNumberOptions = {},
): string {
	return `${formatNumber(min, options)} - ${formatNumber(max, options)} ${unit}`;
}

/**
 * 智能格式化：根据数值大小自动选择是否使用紧凑格式
 * 小于 10,000 显示完整数字，大于等于 10,000 使用 K/M 格式
 *
 * @param value - 要格式化的数字
 * @param options - 格式化选项
 * @returns 格式化后的字符串
 */
export function smartFormat(
	value: number | bigint,
	options: Omit<FormatNumberOptions, "compact"> = {},
): string {
	const numValue = typeof value === "bigint" ? Number(value) : value;

	// 小于 10,000 显示完整数字
	if (numValue < 10000) {
		return formatNumber(value, { ...options, compact: false });
	}

	// 大于等于 10,000 使用紧凑格式
	return formatNumber(value, { ...options, compact: true });
}
